import assistantMarkdown from '@/components/Chat/assistantMarkdown.module.css'
import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { ChatItemV2Assistant } from '@/lib/type'
import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

// CSS for fade-in effect
const fadeInStyle = {
  display: 'inline',
  opacity: 0,
  animation: 'fadeInTypewriter 0.4s forwards',
}

// Add keyframes for fade-in (inject into document head if not present)
if (typeof window !== 'undefined' && !document.getElementById('typewriter-fadein-style')) {
  const style = document.createElement('style')
  style.id = 'typewriter-fadein-style'
  style.innerHTML = `
    @keyframes fadeInTypewriter {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `
  document.head.appendChild(style)
}

function getNextChunk(fullText: string, currentIdx: number): { chunk: string; nextIdx: number } {
  if (currentIdx >= fullText.length) {
    return { chunk: '', nextIdx: currentIdx }
  }
  if (fullText[currentIdx] === '\n') {
    return { chunk: '\n', nextIdx: currentIdx + 1 }
  }
  const messageLength = fullText.length

  // Scale chunk size aggressively for large texts
  let baseChunkSize: number
  if (messageLength < 200) {
    baseChunkSize = Math.max(2, Math.min(8, Math.floor(messageLength * 0.03)))
  } else if (messageLength < 1000) {
    baseChunkSize = Math.max(8, Math.min(20, Math.floor(messageLength * 0.025)))
  } else if (messageLength < 5000) {
    baseChunkSize = Math.max(20, Math.min(80, Math.floor(messageLength * 0.02)))
  } else if (messageLength < 10000) {
    baseChunkSize = Math.max(80, Math.min(200, Math.floor(messageLength * 0.015)))
  } else {
    baseChunkSize = Math.max(200, Math.min(500, Math.floor(messageLength * 0.01)))
  }

  let end = currentIdx + baseChunkSize
  const maxLookahead = baseChunkSize * 2
  for (let i = end; i < Math.min(currentIdx + maxLookahead, fullText.length); i++) {
    if (i > currentIdx && /[.!?,;]\s/.test(fullText.slice(i - 1, i + 1))) {
      end = i + 1
      break
    }
    if (fullText[i] === '\n') {
      end = i + 1
      break
    }
  }
  end = Math.min(end, fullText.length)
  return {
    chunk: fullText.slice(currentIdx, end),
    nextIdx: end,
  }
}

function getPauseForChunk(chunk: string): number {
  const trimmedChunk = chunk.trim()
  if (chunk.includes('\n')) {
    return 250
  }
  if (/[.!?]$/.test(trimmedChunk)) {
    return 180
  }
  if (/[,;:]$/.test(trimmedChunk)) {
    return 120
  }
  if (/ (and|or|but|because|however|therefore) /.test(chunk)) {
    return 80
  }
  const baseSpeed = Math.max(20, Math.min(40, chunk.length * 2))
  return baseSpeed
}

export default function AssistantTypewriterMessage({
  chatMessage,
  shouldAnimate = true,
  onContentChange,
  useTightSpacing = false,
}: {
  chatMessage: ChatItemV2Assistant
  shouldAnimate?: boolean
  onContentChange?: () => void
  useTightSpacing?: boolean
}) {
  const { activeTypewriterMessageId, completeTypewriterAnimation, updateTypewriterProgress } =
    useCopilotContext()
  const [trailingChunks, setTrailingChunks] = useState<string[]>([])
  const trailingChunksRef = useRef<string[]>([])
  const [chunks, setChunks] = useState<string[]>([])
  const [revealedText, setRevealedText] = useState('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const content = chatMessage.content || ''
  const isActivelyAnimating = activeTypewriterMessageId === chatMessage.id
  const persistedState =
    chatMessage.role === 'assistant' && 'typeWriterState' in chatMessage
      ? (chatMessage.typeWriterState ?? 0)
      : 0
  useEffect(() => {
    let idx = 0
    const allChunks: string[] = []
    while (idx < content.length) {
      const { chunk, nextIdx } = getNextChunk(content, idx)
      allChunks.push(chunk)
      idx = nextIdx
    }
    setChunks(allChunks)
  }, [content])
  function getSafeMarkdownBoundary(text: string) {
    const lastBreak = text.lastIndexOf('\n')
    if (lastBreak === -1) return ''
    return text.slice(0, lastBreak + 1)
  }
  useEffect(() => {
    if (!isActivelyAnimating) {
      if (trailingChunks.join('') !== '' || revealedText !== content) {
        setTrailingChunks([])
        trailingChunksRef.current = []
        setRevealedText(content)
      }
      return
    }
    const idx = persistedState
    let chunkIdx = 0
    const allChunks = chunks
    for (let i = 0, pos = 0; i < allChunks.length; i++) {
      if (pos + allChunks[i].length > idx) {
        chunkIdx = i
        break
      }
      pos += allChunks[i].length
    }
    setTrailingChunks([])
    trailingChunksRef.current = []
    const initialText = content.slice(0, idx)
    const safeMarkdown = getSafeMarkdownBoundary(initialText)
    setRevealedText(safeMarkdown)
    const initialTrailing = initialText.slice(safeMarkdown.length)
    if (initialTrailing) {
      setTrailingChunks([initialTrailing])
      trailingChunksRef.current = [initialTrailing]
    }
    function showNextChunk() {
      if (chunkIdx >= allChunks.length) {
        setTrailingChunks([])
        setRevealedText(() => content)
        completeTypewriterAnimation(chatMessage.id)
        return
      }
      const nextChunk = allChunks[chunkIdx]
      const prevTrailing = trailingChunksRef.current
      const newTrailing = [...prevTrailing, nextChunk]
      if (nextChunk.endsWith('\n')) {
        setRevealedText((prevText) => prevText + newTrailing.join(''))
        setTrailingChunks([])
        trailingChunksRef.current = []
      } else if (newTrailing.length > 20) {
        const toKeep = newTrailing.slice(-5)
        const toReveal = newTrailing.slice(0, -5).join('')
        setRevealedText((prevText) => prevText + toReveal)
        setTrailingChunks(toKeep)
        trailingChunksRef.current = toKeep
      } else {
        setTrailingChunks(newTrailing)
        trailingChunksRef.current = newTrailing
      }
      chunkIdx++
      onContentChange?.()
      const pause = getPauseForChunk(nextChunk)
      timeoutRef.current = setTimeout(showNextChunk, pause)
    }
    timeoutRef.current = setTimeout(showNextChunk, 0)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [
    isActivelyAnimating,
    chatMessage.id,
    completeTypewriterAnimation,
    updateTypewriterProgress,
    persistedState,
    content,
    onContentChange,
    chunks,
  ])
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])
  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.3 },
      }
    : {}
  return (
    <WrapperComponent {...animationProps}>
      <div className="flex w-full gap-x-1">
        <div className="flex grow flex-col gap-y-2 overflow-hidden">
          <div className={`${useTightSpacing ? 'mb-1' : 'mb-2'} max-w-[80%] self-start`}>
            <div
              className="font-hanken-grotesk relative rounded-3xl px-4 py-3 text-[#2D1A47]"
              style={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                fontFamily: 'Hanken Grotesk, sans-serif',
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              {revealedText && (
                <span style={{ display: 'inline' }}>
                  <EnhancedMarkdown
                    className={assistantMarkdown['wm-markdown']}
                    style={{
                      background: 'transparent',
                      display: 'inline',
                    }}
                  >
                    {revealedText.replace(/(?<!\n)\n(?!\n)/g, '  \n')}
                  </EnhancedMarkdown>
                </span>
              )}
              {trailingChunks.length > 0 && (
                <span style={fadeInStyle}>{trailingChunks.join('')}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </WrapperComponent>
  )
}
